<template>
    <form-group
        :group-classes="formGroupClasses"
        :horizontal="horizontal"
        :inline="inline"
        :label="label"
        :label-classes="{ required }"
        class="datepicker"
    >
        <div v-if="readonly">{{ modelValue ? formattedDate : placeholder }}</div>
        <DatePickerRoot
            v-else
            v-model="selectedValue"
            v-model:open="isOpen"
            :close-on-date-select="true"
            :disabled="disabled"
            :fixed-weeks="true"
            :locale="currentLocale"
            :max-value="maxValueDV"
            :min-value="minValueDV"
            granularity="day"
        >
            <DatePickerAnchor>
                <div
                    v-if="inline"
                    ref="fieldRef"
                    :class="{ 'pointer-events-none': disabled }"
                    class="flex items-center gap-1"
                    @focusin="isFieldFocused = true"
                    @focusout="onFieldFocusOut"
                >
                    <DatePickerField v-slot="{ segments }" class="flex items-center text-accent">
                        <template v-for="segment in segments" :key="segment.part">
                            <DatePickerInput v-if="segment.part === 'literal'" :part="segment.part" class="px-0 text-accent">
                                {{ segment.value }}
                            </DatePickerInput>
                            <DatePickerInput v-else :part="segment.part" class="text-accent">
                                {{ segment.value }}
                            </DatePickerInput>
                        </template>

                        <DatePickerTrigger as-child>
                            <Button :disabled="disabled" class="ml-1 !h-auto !min-h-0 border-0 p-0" variant="link">
                                <CalendarIcon class="h-4 w-4" />
                            </Button>
                        </DatePickerTrigger>
                    </DatePickerField>

                    <Button
                        v-if="selectedValue && deletable"
                        class="!h-auto !min-h-0 border-0 p-0 text-danger"
                        variant="link"
                        @click.stop="clearDate"
                    >
                        <XIcon class="h-4 w-4" />
                    </Button>
                </div>
                <div
                    v-else
                    ref="fieldRef"
                    :class="[
                        'flex w-full rounded-xl border bg-white',
                        { 'pointer-events-none': disabled },
                        !isValid && !hasValue ? 'border-danger' : '',
                        isOpen ? '!border-accent' : '',
                        buttonClasses,
                    ]"
                    @focusin="isFieldFocused = true"
                    @focusout="onFieldFocusOut"
                >
                    <DatePickerField v-slot="{ segments }" class="flex flex-grow items-center gap-0.5 px-2 py-3 text-sm text-[#525252]">
                        <DatePickerTrigger as-child>
                            <button class="mr-1 shrink-0" type="button">
                                <CalendarIcon :class="!isValid && !isOpen && !hasValue ? 'text-danger' : 'text-accent'" class="h-4 w-4" />
                            </button>
                        </DatePickerTrigger>

                        <template v-for="segment in segments" :key="segment.part">
                            <DatePickerInput v-if="segment.part === 'literal'" :part="segment.part" class="text-muted-foreground px-0">
                                {{ segment.value }}
                            </DatePickerInput>
                            <DatePickerInput v-else :part="segment.part">
                                {{ segment.value }}
                            </DatePickerInput>
                        </template>
                    </DatePickerField>

                    <button
                        v-if="selectedValue && deletable"
                        :disabled="disabled"
                        class="flex shrink-0 items-center border-l px-2 py-3 text-[#525252] hover:text-[#525252]/70"
                        type="button"
                        @click.stop="clearDate"
                    >
                        <XIcon class="h-4 w-4" />
                    </button>
                </div>
            </DatePickerAnchor>

            <DatePickerContent
                :align-flip="true"
                :avoid-collisions="true"
                :class="popoverClasses"
                :portal="{ to: portalTarget }"
                :prioritize-position="true"
                :side-offset="4"
                align="start"
                @focus-outside="onPopoverFocusOutside"
            >
                <DatePickerCalendar v-slot="{ grid, weekDays }">
                    <DatePickerHeader>
                        <DatePickerPrev :prev-page="(date) => date.subtract({ years: 1 })">
                            <ChevronsLeft class="h-4 w-4" />
                        </DatePickerPrev>
                        <DatePickerPrev />
                        <DatePickerHeading />
                        <DatePickerNext />
                        <DatePickerNext :next-page="(date) => date.add({ years: 1 })">
                            <ChevronsRight class="h-4 w-4" />
                        </DatePickerNext>
                    </DatePickerHeader>

                    <div class="mt-4 flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
                        <DatePickerGrid v-for="month in grid" :key="month.value.toString()">
                            <DatePickerGridHead>
                                <DatePickerGridRow>
                                    <DatePickerHeadCell v-for="day in weekDays" :key="day">
                                        {{ day }}
                                    </DatePickerHeadCell>
                                </DatePickerGridRow>
                            </DatePickerGridHead>
                            <DatePickerGridBody>
                                <DatePickerGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="mt-2 w-full">
                                    <DatePickerCell v-for="weekDate in weekDates" :key="weekDate.toString()" :date="weekDate">
                                        <DatePickerCellTrigger :day="weekDate" :month="month.value" />
                                    </DatePickerCell>
                                </DatePickerGridRow>
                            </DatePickerGridBody>
                        </DatePickerGrid>
                    </div>
                </DatePickerCalendar>
            </DatePickerContent>
        </DatePickerRoot>
        <div v-if="description" class="mt-2 text-muted">
            <small>{{ description }}</small>
        </div>
        <div v-if="invalidFeedback" class="mt-1 text-sm text-[13px] text-danger" v-text="invalidFeedback" />
    </form-group>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import FormGroup from '@/Components/FormGroup.vue';
import { trans } from '@/mixins/i18n';
import { DateTime } from 'luxon';
import { Calendar as CalendarIcon, ChevronsLeft, ChevronsRight, X as XIcon } from '@lucide/vue';

import { Button } from '@/Components/ui/button';
import { DatePickerAnchor, DatePickerRoot } from 'reka-ui';
import {
    DatePickerCalendar,
    DatePickerCell,
    DatePickerCellTrigger,
    DatePickerContent,
    DatePickerField,
    DatePickerGrid,
    DatePickerGridBody,
    DatePickerGridHead,
    DatePickerGridRow,
    DatePickerHeadCell,
    DatePickerHeader,
    DatePickerHeading,
    DatePickerInput,
    DatePickerNext,
    DatePickerPrev,
    DatePickerTrigger,
} from '@/Components/ui/date-picker';
import { CalendarDate, getLocalTimeZone, parseDate } from '@internationalized/date';

const props = defineProps({
    label: { type: String, default: '' },
    variant: { type: String, default: 'default' },
    disabled: { type: Boolean, default: false },
    type: { type: String, default: 'text' },
    invalidFeedback: { type: String, default: '' },
    modelValue: { type: [Date, Object, String, null] },
    required: { type: Boolean, default: false },
    horizontal: { type: [Boolean, Object], default: true },
    placeholder: { type: String, default: () => trans('general.form.select.placeholder') },
    description: { type: String, default: '' },
    minDate: { type: [Object, String] },
    maxDate: { type: [Object, String] },
    deletable: { type: Boolean, default: true },
    color: { type: String, default: 'primary' },
    formGroupClasses: { type: [String, Object, Array] },
    buttonClasses: { type: [String, Object, Array] },
    inline: { type: Boolean, default: false },
    popoverClasses: { type: [String, Object, Array] },
    readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);
const isOpen = ref(false);
const isFieldFocused = ref(false);
const fieldRef = ref(null);

const instance = getCurrentInstance();

const currentLocale = computed(() => {
    const maybeLocale = instance?.proxy?.currentLocale;
    return maybeLocale || 'en';
});

const isValid = computed(() => props.invalidFeedback === null || (props.invalidFeedback || '').trim() === '');
const hasValue = computed(() => props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '');

function toDateValue(dt) {
    if (!dt || !dt.isValid) return undefined;
    const iso = dt.toISODate();
    return iso ? parseDate(iso) : undefined;
}

function toDateTime(value) {
    if (!value) return null;
    const anyVal = value;
    let jsDate = null;
    if (typeof anyVal?.toDate === 'function') {
        jsDate = anyVal.toDate(getLocalTimeZone());
    } else if (typeof anyVal?.year === 'number' && typeof anyVal?.month === 'number' && typeof anyVal?.day === 'number') {
        jsDate = new Date(anyVal.year, anyVal.month - 1, anyVal.day);
    }
    if (!jsDate) return null;
    const dt = DateTime.fromJSDate(jsDate);
    return dt.isValid ? dt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) : null;
}

function coerceToDateTime(input) {
    if (!input) return null;
    if (typeof input === 'string') {
        const dt = DateTime.fromISO(input);
        return dt.isValid ? dt : null;
    }
    if (typeof input === 'object' && input?.isValid !== undefined) {
        const dt = input;
        return dt.isValid ? dt : null;
    }
    if (input instanceof Date) {
        const dt = DateTime.fromJSDate(input);
        return dt.isValid ? dt : null;
    }
    return null;
}

const selectedValue = ref(toDateValue(coerceToDateTime(props.modelValue)));

const globalMinDate = new CalendarDate(1970, 1, 1); // MySQL TIMESTAMP column minimum
const globalMaxDate = new CalendarDate(2038, 1, 19); // MySQL TIMESTAMP column maximum
const minValueDV = computed(() => {
    const userMin = toDateValue(coerceToDateTime(props.minDate));
    return userMin ?? globalMinDate;
});
const maxValueDV = computed(() => {
    const userMax = toDateValue(coerceToDateTime(props.maxDate));
    return userMax ?? globalMaxDate;
});

const formattedDate = computed(() => {
    const mv = coerceToDateTime(props.modelValue);
    if (!mv || mv.isValid === false) return '';
    const filters = instance?.proxy?.$filters;
    if (filters?.fullDate) {
        return filters.fullDate(mv, currentLocale.value);
    }
    return mv.toLocaleString(DateTime.DATE_FULL);
});

// Distinguishes explicit clear (X button) from reka-ui auto-clearing
// incomplete segments on blur, so the watcher can skip restore logic.
let isIntentionalClear = false;

function clearDate() {
    isIntentionalClear = true;
    selectedValue.value = undefined;
    emit('update:modelValue', null);
}

// Compare at day granularity: next is always midnight, while min/max may carry a
// time-of-day (ATOM dates) — without startOf('day') a same-day value would clamp forever.
function dayBounds() {
    return {
        minDt: (coerceToDateTime(props.minDate) ?? DateTime.fromISO('1970-01-02')).startOf('day'),
        maxDt: (coerceToDateTime(props.maxDate) ?? DateTime.fromISO('2038-01-19')).startOf('day'),
    };
}

function emitIfChanged(next) {
    const currentIso = coerceToDateTime(props.modelValue)?.toISODate();
    const nextIso = next?.toISODate();
    if (currentIso === nextIso) return;
    emit('update:modelValue', next?.toFormat('yyyy-MM-dd') ?? null);
}

// Clamp out-of-range values to the nearest boundary, or restore the last valid value
// when reka-ui auto-clears an incomplete entry. Setting selectedValue re-runs the watcher,
// which then emits the settled value.
function normalizeAndApply(val) {
    const next = toDateTime(val);
    const { minDt, maxDt } = dayBounds();

    // Contradictory bounds (e.g. start date moved past due date) — clamping would bounce
    // between the two boundaries forever, so emit as-is and let form validation flag it.
    if (next && minDt > maxDt) {
        emitIfChanged(next);
        return;
    }

    if (next && (next < minDt || next > maxDt)) {
        selectedValue.value = toDateValue(next < minDt ? minDt : maxDt);
        return;
    }

    if (!next) {
        const current = toDateValue(coerceToDateTime(props.modelValue));
        if (current) {
            selectedValue.value = current;
            return;
        }
    }

    emitIfChanged(next);
}

function onFieldFocusOut(event) {
    // focusout bubbles for moves between segments too; only treat it as a real blur
    // when focus actually leaves the field, then enforce min/max on the typed value.
    if (event.currentTarget.contains(event.relatedTarget)) return;
    isFieldFocused.value = false;
    normalizeAndApply(selectedValue.value);
}

// A native <dialog> shown via showModal() (InertiaUI modals) renders in the browser's
// top layer, which paints above all non-top-layer content regardless of z-index. The
// popover normally portals to <body> — outside the top layer — so it would render behind
// the modal. Teleporting it into the <dialog> instead keeps it inside the promoted
// subtree, above the modal, while non-dialog pages keep the default body target.
const portalTarget = computed(() => fieldRef.value?.closest?.('dialog') ?? undefined);

// Older, non-native-dialog modals still portal the popover out to <body>. Opening the
// picker there moves focus into that portaled content; the modal's own focus trap yanks
// it back, and reka reads that as focusOutside and dismisses — so on Safari/Firefox the
// calendar flashes and never opens. Suppress the focus-driven dismiss only in that case;
// on plain pages let focus-out close it as usual. Click-outside, Escape and date-selection
// still close the popover everywhere.
function onPopoverFocusOutside(event) {
    if (fieldRef.value?.closest?.('[role="dialog"],[data-inertiaui-modal-id]') && !portalTarget.value) {
        event.preventDefault();
    }
}

watch(
    () => props.modelValue,
    (newVal) => {
        const newIso = coerceToDateTime(newVal)?.toISODate();
        const currentIso = toDateTime(selectedValue.value)?.toISODate();
        if (newIso === currentIso) return;
        selectedValue.value = toDateValue(coerceToDateTime(newVal));
    },
);

watch(selectedValue, (val) => {
    if (isIntentionalClear) {
        isIntentionalClear = false;
        return;
    }

    // While the user is typing, never snap the value back — multi-digit entry passes
    // through out-of-range intermediates (typing "17" briefly reads as the 1st).
    // Emit only complete, in-range values; defer clamp/restore to blur.
    if (isFieldFocused.value) {
        const next = toDateTime(val);
        const { minDt, maxDt } = dayBounds();
        if (next && next >= minDt && next <= maxDt) {
            emitIfChanged(next);
        }
        return;
    }

    normalizeAndApply(val);
});
</script>
